import React, { useEffect, useRef, useState } from "react"
import classNames from "classnames"
import { X } from "react-feather"
import { connect } from "react-redux"

import API from "api"
import { mapDispatchProps, mapStateToProps } from "store/store"
import { useLanguage } from "@languages/index"

import Button from "@components/button"
import Checkbox from "@components/checkbox"
import MatchRatingStyleEditorWithSteps from "@components/MatchRatingEditorWithSteps"

const ICON_STAR_IN_ROUNDED_SQUARE = "/assets/icons/ic-star-in-rounded-square.svg";
const ICON_CHECK_IN_ROUNDED_SQUARE = "/assets/icons/ic-check-in-rounded-square.svg";
const ICON_X_IN_ROUNDED_SQUARE = "/assets/icons/ic-x-in-rounded-square.svg";


const OverlayMatchRatingSettingWithSteps = (props: {
  [key:string]:any,
  hasCloseButton?: boolean,
  shouldShowCautionModal?: boolean,
  onCompleteSubmit?: (styles, categories) => void,
}) => {

  const language = useLanguage()
  const languageTexts = { ...language.common, ...language.component.overlayMatchRatingSetting }

  const stylesRef = useRef([]);
  const categoriesRef = useRef([]);
  const [selectedStylesCount, setSelectedStylesCount] = useState(0); // only for button states

  const [isHidden, setIsHidden] = useState(true)
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmittingState] = useState(isSubmittingRef.current);
  const [isMatchRatingsEditorLoaded, setIsMatchRatingsEditorLoaded] = useState(false);

  const [step, setStep] = useState(0);

  const step_data_map = {
    0: {
      icon: ICON_STAR_IN_ROUNDED_SQUARE,
      title: languageTexts.index_text_1,
    },
    1: {
      icon: ICON_CHECK_IN_ROUNDED_SQUARE,
      title: languageTexts.index_text_2,
    },
    2: {
      icon: ICON_X_IN_ROUNDED_SQUARE,
      title: languageTexts.index_text_3,
    },
  }

  /********************************************************************
   **  STATE FUNCTIONS
   ********************************************************************/
  const setIsSubmitting = (newValue:boolean) => {
    isSubmittingRef.current = newValue;
    setIsSubmittingState(newValue);
  }



  /********************************************************************
   **  useEffects
   ********************************************************************/

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }

    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, []);


  /********************************************************************
   **  REQUESTS
   ********************************************************************/

  const submit = async () => {
    if (isSubmittingRef.current) {
      props.showModal({
        text: languageTexts.is_requesting,
      });
      return;
    }
    setIsSubmitting(true);
    try {
      await API.MatchRating.bulkStoreOrUpdateRatings({
        brandToken: props.auth.token,
        styles: stylesRef.current,
        categories: categoriesRef.current,
      });
      await API.Brand.update({
        brandToken: props.auth.token,
        hasExperiencedMatchRatingCreate: true,
      });
      props.setBrand({ hasExperiencedMatchRatingCreate: true });

      if (props.onCompleteSubmit) {
        props.onCompleteSubmit(stylesRef.current, categoriesRef.current);
      }

      props.showModal({
        text: languageTexts.index_text_4,
        confirm: () => {
          setIsSubmitting(false);
          closeOverlays();
        }
      })
    } catch (error) {
      props.showModal({
        text: error.message,
      });
      setIsSubmitting(false);
    }
  }



  /********************************************************************
   **  HELPER FUNCTIONS
   ********************************************************************/

  const closeOverlays = () => {
    if (props.preferences.isMobile) {
      props.closeSheet()
    } else {
      props.closePopup()
    }
  }


  
  /********************************************************************
   **  EVENT HANDLERS
   ********************************************************************/



  /********************************************************************
   **  RENDERERS
   ********************************************************************/

  const CautionModal = (componentProps:{
    [key:string]:any,
    onConfirm:() => void,
    onCancel:() => void,
  }) => {
    const [doNotShowAgain, setDoNotShowAgain] = useState(false);

    return (
      <div className="flex flex-col justify-center align-center p-8 bg-white">
        <div className="content flex flex-col">
          <span className="font-bold text-[22px] text-black">
            {languageTexts.index_text_5}
          </span>
        </div>
        <div className="mt-4 text-[14px] text-black whitespace-pre-wrap">
          {languageTexts.index_text_6}
        </div>
        <div className="mt-7 flex flex-row gap-[10px]">
          <Checkbox
            checked={doNotShowAgain}
            text={languageTexts.index_text_7}
            onClick={() => {
              setDoNotShowAgain(!doNotShowAgain);
            }}
          />
        </div>
        <div className="mt-6 buttons flex gap-1.5">
          <Button
            className={"min-w-[10rem] py-3 flex justify-center text-center text-base"}
            borderColor={"BLACK"}
            text={languageTexts.cancel}
            onClick={() => {
              componentProps.onCancel();
            }}
          />
          <Button
            className={"min-w-[10rem] py-3 flex justify-center text-center text-base"}
            borderColor={"BLACK"}
            buttonColor={"BLACK"}
            textColor={"WHITE"}
            text={languageTexts.confirm}
            onClick={() => {
              if (doNotShowAgain) {
                props.setPreferences({
                  isMatchRatingSettingCautionModalDisabled: true,
                })
              }
              componentProps.onConfirm();
            }}
          />
        </div>
      </div>
    )
  }


  const renderStepButtons = (step:number, setStep:any) => {
    switch (step) {
      case 0:
        const isEnabledStep0Button = !isSubmitting && selectedStylesCount > 0
        return (
          <Button
            text={languageTexts.button_next_2}
            buttonColor={isEnabledStep0Button ? "black" : "#d8d8d8"}
            textColor={"white"}
            onClick={() => {
              setStep(1)
            }}
          />
        )
      
      case 1:
        return (
          <>
            <Button
              className="flex-initial py-3 mx-auto flex justify-center items-center"
              style={{
                width: '100px'
              }}
              text={languageTexts.button_prev_4}
              buttonColor={!isSubmitting ? "white" : "#d8d8d8"}
              borderColor={!isSubmitting ? "black" : ""}
              textColor={!isSubmitting ? "black" : "#d8d8d8"}
              onClick={() => {
                setStep(0)
              }}
            />
            <Button
              className="flex-grow py-3 mx-auto flex justify-center items-center"
              text={languageTexts.button_next_2}
              buttonColor={!isSubmitting ? "black" : "#d8d8d8"}
              borderColor={!isSubmitting ? "black" : ""}
              textColor={!isSubmitting ? "white" : "#d8d8d8"}
              onClick={() => {
                setStep(2)
              }}
            />
          </>
        )
        
      case 2:
        return (
          <>
            <Button
              className="flex-initial py-3 mx-auto flex justify-center items-center"
              style={{
                width: '100px'
              }}
              text={languageTexts.button_prev_4}
              buttonColor={!isSubmitting ? "white" : "#d8d8d8"}
              borderColor={!isSubmitting ? "black" : ""}
              textColor={!isSubmitting ? "black" : "#d8d8d8"}
              onClick={() => {
                setStep(1)
              }}
            />
            <Button
              className="flex-grow py-3 mx-auto flex justify-center items-center"
              text={languageTexts.done}
              buttonColor={isSubmitting ? "#d8d8d8" : "black"}
              textColor={"white"}
              onClick={() => {
                if (
                  props.preferences.isMatchRatingSettingCautionModalDisabled != true &&
                  props.shouldShowCautionModal === true 
                ) {
                  props.showModal({
                    hideCloseButton: true,
                    content: () => {
                      return (
                        <CautionModal
                          {...props}
                          onCancel={() => {
                            props.closeModal();
                          }}
                          onConfirm={() => {
                            submit();
                          }}
                        />
                      );
                    },
                  })
                } else {
                  submit();
                }
              }}
            />
          </>
        )

    
      default:
        return null;
    }
  }

  return (
    <div
      className={classNames([
        "relative bg-white rounded-lg shadow-md max-h-[80vh] overflow-scroll",
        !isMatchRatingsEditorLoaded ? "" : "py-[50px]"
      ])}
      style={{ width: windowWidth < 480 ? `${windowWidth}px` : "480px" }}
    >
      {props.hasCloseButton && (
        <div
          className="absolute right-0 top-0 p-[30px] cursor-pointer"
          onClick={() => {
            props.closeOverlays()
          }}
        >
          <X strokeWidth={1} />
        </div>
      )}

      {isMatchRatingsEditorLoaded && (
        <>
          <div className="px-[50px] flex justify-evenly gap-[6px]">
            {Object.keys(step_data_map).map((v,i) => (
              <div key={i} className={classNames([
                "w-full h-[3px]",
                (i === step) ? "bg-black" : "bg-[#E8E8E8]"
              ])}></div>
            ))}
          </div>

          <div className="mt-[30px] px-[50px] ">
            <img
              src={step_data_map[step].icon} 
              alt="ic-check_in_rounded_square"
              className="w-[40px] h-[40px]"
            />
          </div>

          <div className="mt-[10px] px-[50px] text-[22px] font-bold whitespace-pre-wrap">
            {step_data_map[step].title}
          </div>
        </>
      )}

      <MatchRatingStyleEditorWithSteps 
        {...props}
        containerClassName={!isMatchRatingsEditorLoaded ? "" : "mt-[20px] mb-[50px] px-[50px]"}
        step={step}
        onChangeStyles={(newStyles) => {
          stylesRef.current = newStyles;
          setSelectedStylesCount(newStyles.filter((style) => style.rating > 0).length)
        }}
        onChangeCategories={(newCategories) => {
          categoriesRef.current = newCategories;
        }}
        onLoaded={(isLoaded) => {
          setIsMatchRatingsEditorLoaded(isLoaded);
        }}
      />

      {isMatchRatingsEditorLoaded && (
        <div className="px-[50px] w-full flex gap-[6px]">
          {renderStepButtons(step, setStep)}
        </div>
      )}
    </div>
  )
}

export default connect(mapStateToProps, mapDispatchProps)(OverlayMatchRatingSettingWithSteps)
